// CORE
import React from "react";
import { Button, Result } from "antd";

/**
 * Catches render/lifecycle errors thrown anywhere below it so a single broken
 * screen shows a recoverable message instead of unmounting the whole CMS and
 * leaving the user on a blank white page (bug report module 1, issue 5).
 *
 * Error boundaries have to be class components - there is no hook equivalent.
 */
class ErrorBoundary extends React.Component {
	constructor(props) {
		super(props);
		this.state = { hasError: false, error: null };
		this.handleRetry = this.handleRetry.bind(this);
		this.handleReload = this.handleReload.bind(this);
	}

	static getDerivedStateFromError(error) {
		return { hasError: true, error };
	}

	componentDidCatch(error, errorInfo) {
		// Keep the stack in the console so it is still reportable, but never let
		// the throw escape to the root and blank the page.
		// eslint-disable-next-line no-console
		console.error("[CMS] Unhandled UI error:", error, errorInfo);
	}

	componentDidUpdate(prevProps) {
		// When the route changes, give the subtree a fresh chance to render.
		if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
			// eslint-disable-next-line react/no-did-update-set-state
			this.setState({ hasError: false, error: null });
		}
	}

	handleRetry() {
		this.setState({ hasError: false, error: null });
	}

	handleReload() {
		window.location.reload();
	}

	render() {
		if (!this.state.hasError) {
			return this.props.children;
		}

		if (this.props.fallback) {
			return this.props.fallback;
		}

		return (
			<Result
				status="warning"
				title={this.props.title || "Something went wrong on this screen"}
				subTitle="The page could not be displayed. You can try again, or reload the CMS if the problem continues."
				extra={[
					<Button type="primary" key="retry" onClick={this.handleRetry}>
						Try again
					</Button>,
					<Button key="reload" onClick={this.handleReload}>
						Reload
					</Button>,
				]}
			/>
		);
	}
}

export default ErrorBoundary;
